import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  CHART_DESCRIPTION_MIN_LENGTH,
  useCustomChartData,
  type ChartBindingValue,
  type ChartCategoryColors,
  type ChartRule,
  type ChartRuleResolveError,
  type ChartValueColorRule,
  type CustomChartBarOrientation,
  type CustomChartSize,
  type CustomChartType,
} from '@/entities/dashboards';
import { useHasPermission } from '@/entities/profile';
import {
  defaultSizeForType,
  isResolvedChart,
  type ChartAppearanceValues,
  type ChartRuleDraft,
  type PresetChartFormValues,
} from '../../model/dashboardPresetForm';
import { chartBindingDataParams } from '../../model/useCustomChartBindings';
import { useChartRuleResolution } from '../../model/useChartRuleResolution';
import type { ChartSizeChange } from '../../model/useDashboardPresetEditor';
import type { CustomChartPanelFilters } from '../DashboardPresetGrid/CustomChartCard';

/** Типы диаграмм, результат которых собирается текстом с подстановками значений выборки. */
const TEXT_RESULT_CHART_TYPES: CustomChartType[] = ['metric', 'text-summary'];

type Params = {
  /** Диаграмма, свойства которой правятся */
  chart: PresetChartFormValues;
  /** Фильтры панели страницы: ими диаграмма наполнена, ими же известны значения её выборки */
  filters: CustomChartPanelFilters;
  /** Описание истолковано — диаграмма перерисовывается на своём месте сразу */
  onResolve: (chart: PresetChartFormValues) => void;
  /** Введённое описание остаётся за диаграммой, когда блок сворачивают, не сохранив свойств */
  onKeepDescription: (chartId: string, description: string) => void;
  /** Выбранный размер встаёт на сетке сразу, не дожидаясь сохранения свойств */
  onResize: (change: ChartSizeChange) => void;
  /** Заданное оформление диаграмма принимает сразу — как и размер */
  onRestyle: (appearance: ChartAppearanceValues) => void;
  /** Правки приняты — диаграмма с закреплённым правилом возвращается в состав дашборда */
  onSave: (chart: PresetChartFormValues) => void;
  onCancel: () => void;
};

/**
 * Правка свойств диаграммы: значения формы, истолкование описания и проверка правила под тип.
 *
 * Правленое описание истолковывается по потере фокуса — тем же событием, что и у заводимой
 * диаграммы: пользователь сразу видит на её месте, что получилось, и правит описание дальше,
 * если понят не так. Сохранению свойств остаётся закрепить состояние диаграммы за дашбордом, и
 * к модели оно уже не обращается. Отказ истолкования прежнего правила не отменяет — диаграмма
 * показана тем, что за ней закреплено, пока новое описание не понято.
 *
 * Смена типа правило не перестраивает, но сразу же проверяется наполнением под новый тип: не
 * отвечающий правилу тип виден по выбору, а не пустым блоком на сохранённом дашборде.
 *
 * Запрос диаграммы и текст её результата правятся не значениями формы: правилами формы они не
 * проверяются, а сохраняются иначе — заверением либо вовсе без обращения к бэкенду.
 */
export const useChartPropertiesForm = ({
  chart,
  filters,
  onResolve,
  onKeepDescription,
  onResize,
  onRestyle,
  onSave,
  onCancel,
}: Params) => {
  const { resolveDescription, verifyChartType, signChart, isResolving } = useChartRuleResolution();
  const canEditQuery = useHasPermission('ADMIN_DASHBOARD_CHART_QUERY');

  const { control, watch, setValue, getValues, handleSubmit } = useForm<PresetChartFormValues>({
    defaultValues: chart,
  });
  const [error, setError] = useState<ChartRuleResolveError | null>(null);
  const [ruleDraft, setRuleDraft] = useState<ChartRuleDraft>({
    query: chart.rule?.query ?? '',
    template: chart.rule?.template ?? '',
  });
  // Размер и оформление на момент разворачивания блока — к ним возвращает отказ от правки.
  const initialSizeRef = useRef<ChartSizeChange>({ size: chart.size, height: chart.height });
  const initialAppearanceRef = useRef<ChartAppearanceValues>({
    barOrientation: chart.barOrientation,
    categoryColors: chart.categoryColors,
    valueColorRules: chart.valueColorRules,
  });
  // Ответ, пришедший после сворачивания блока, применять уже некуда.
  const isBlockOpenRef = useRef(true);
  // Диаграмма состава на последний рендер: пока идёт истолкование, её могли растянуть или
  // перетащить, и заданное на сетке нельзя откатывать к тому, что было в начале обращения.
  const chartRef = useRef(chart);

  const chartType = watch('chartType');
  const size = watch('size');
  const description = watch('description');
  const resolvedDescription = watch('resolvedDescription');
  const resolvedChartType = watch('resolvedChartType');
  const summary = watch('summary');
  const bindings = watch('bindings');
  const sliceValue = watch('sliceValue');
  const sourceChartId = watch('sourceChartId');
  const barOrientation = watch('barOrientation');
  const categoryColors = watch('categoryColors');
  const valueColorRules = watch('valueColorRules');
  const isTemplateShown = TEXT_RESULT_CHART_TYPES.includes(chartType);
  // Повторяемая часть текста показывает строки выборки — их перечисляет сводка, а показатель
  // показывает одно значение, и повторять в нём нечего.
  const isRowSectionShown = chartType === 'text-summary';
  // Разрез у диаграммы один: несколько разрезов в шапке не поместились бы, и правило их не даёт.
  const sliceBinding = bindings.find((binding) => binding.kind === 'slice');
  // Место под выбранное требует ведущей диаграммы — без неё наполнять связанную нечем.
  const isSourceChartShown = bindings.some((binding) => binding.kind === 'selection');
  // Правило отвечает и набранному описанию, и выбранному типу — диаграмме есть с чем идти в состав.
  const isRuleActual = description.trim() === resolvedDescription.trim() && chartType === resolvedChartType;

  // Значения выборки и категории берутся из наполнения диаграммы: правило сохранённой диаграммы
  // через истолкование больше не проходит. Наполнение то же самое, что у карточки на сетке, —
  // теми же местами подстановки, поэтому берётся из кэша, а не запрашивается заново.
  const { data } = useCustomChartData(
    chart.rule
      ? {
          chartType: chart.chartType,
          rule: chart.rule,
          signature: chart.signature || null,
          ...filters,
          ...chartBindingDataParams(chart),
        }
      : null,
  );
  const columns = chart.columns.length > 0 ? chart.columns : (data?.columns ?? []);
  // Цвет задаётся категориям последней полученной выборки: заранее их знает только наполнение.
  const categories =
    data && (data.chartType === 'bar' || data.chartType === 'pie')
      ? data.payload.points.map((point) => point.label)
      : [];

  // Размер, высота и начало строки задаются и на самой сетке — растягиванием границ и
  // перетаскиванием. Блок при этом остаётся развёрнутым, поэтому форма принимает заданное там:
  // иначе сохранение свойств вернуло бы диаграмму к величинам на момент разворачивания.
  useEffect(() => {
    chartRef.current = chart;
    setValue('size', chart.size);
    setValue('height', chart.height);
    setValue('startsRow', chart.startsRow);
  }, [chart, setValue]);

  // Признак поднимается на каждом монтировании, а не только при заведении: в режиме строгих
  // проверок React снимает эффект сразу после первого монтирования и ставит его заново, и без
  // подъёма признак остался бы снятым — тогда отбрасывался бы каждый ответ, а не только пришедший
  // после сворачивания блока.
  useEffect(() => {
    isBlockOpenRef.current = true;
    return () => {
      isBlockOpenRef.current = false;
    };
  }, []);

  // Введённое описание закрепляется за диаграммой на сворачивании блока — им заканчивается любая
  // правка свойств, и сохранением, и отказом от него. Само истолкование к этому времени уже
  // прошло: описание истолковывается потерей фокуса, а не сворачиванием блока.
  useEffect(
    () => () => onKeepDescription(chart.chartId, getValues('description')),
    [chart.chartId, getValues, onKeepDescription],
  );

  /** Кладёт итог обращения к бэкенду в значения формы: правило, его пересказ и значения выборки. */
  const applyToForm = (resolved: PresetChartFormValues) => {
    setValue('rule', resolved.rule);
    setValue('signature', resolved.signature);
    setValue('summary', resolved.summary);
    setValue('columns', resolved.columns);
    setValue('bindings', resolved.bindings);
    setValue('sliceValue', resolved.sliceValue);
    setValue('resolvedDescription', resolved.resolvedDescription);
    setValue('resolvedChartType', resolved.resolvedChartType);
  };

  /**
   * Отдаёт истолкованное правило в состав правящегося дашборда — диаграмма перерисовывается им
   * сразу. В состав идёт только истолкованное: название и тип диаграмма принимает сохранением
   * свойств, и отказ от правки не должен уносить их с собой.
   */
  const applyResolvedToDraft = (resolved: PresetChartFormValues) => {
    const current = chartRef.current;
    onResolve({
      ...current,
      description: resolved.description,
      rule: resolved.rule,
      signature: resolved.signature,
      summary: resolved.summary,
      resolvedDescription: resolved.resolvedDescription,
      // Правило проверено для типа, которым истолковывали, а в составе тип пока прежний — им и
      // помечаем: иначе свойства диаграммы, свёрнутые без сохранения, было бы не закрепить.
      resolvedChartType: current.chartType,
      columns: resolved.columns,
      bindings: resolved.bindings,
      sliceValue: resolved.sliceValue,
    });
  };

  const handleChangeDescription = (value: string) => setValue('description', value, { shouldDirty: true });

  /**
   * Истолкование правленого описания: клик мимо поля повторного обращения к модели не стоит,
   * а после отказа повтор разрешён — причиной мог быть и сбой связи.
   */
  const handleResolveDescription = async () => {
    const values = getValues();
    const text = values.description.trim();
    if (text.length < CHART_DESCRIPTION_MIN_LENGTH) return;
    if (text === values.resolvedDescription.trim() && !error) return;

    const outcome = await resolveDescription(values, values.description, {
      // Образцом идёт текст, показанный в поле, — в том числе ещё не сохранённый; у типов без
      // текста результата поля нет, и образца тоже.
      text: isTemplateShown ? ruleDraft.template : '',
      columns,
    });
    if (!isBlockOpenRef.current) return;
    setError(outcome.error);
    if (outcome.error) return;
    applyToForm(outcome.chart);
    applyResolvedToDraft(outcome.chart);
    // Правленый вручную запрос относился к прежнему правилу — в форме встаёт составленный системой;
    // текст результата встаёт тот, что оставило истолкование: прежний либо составленный заново.
    setRuleDraft({ query: outcome.chart.rule?.query ?? '', template: outcome.chart.rule?.template ?? '' });
  };

  /** Ставит диаграмме размер: и в форму, и на сетку — выбранную долю видно сразу. */
  const applySize = (nextSize: CustomChartSize) => {
    setValue('size', nextSize, { shouldDirty: true });
    onResize({ size: nextSize });
  };

  /**
   * Выбранный тип принимается сразу вместе с рекомендуемым для него размером, а правило под него
   * тут же проверяется наполнением: размер показателя тесен таблице, а размер таблицы избыточен
   * показателю.
   */
  const handleChangeType = async (nextType: CustomChartType) => {
    setValue('chartType', nextType, { shouldDirty: true });
    applySize(defaultSizeForType(nextType));

    const values = { ...getValues(), chartType: nextType };
    if (!isResolvedChart(values) || values.resolvedChartType === nextType) {
      setError(null);
      return;
    }
    // Правило проверкой не меняется, поэтому в состав дашборда идти незачем: тип диаграмма
    // принимает сохранением свойств, а форме довольно знать, что тип правилу отвечает.
    const outcome = await verifyChartType(values, nextType);
    if (!isBlockOpenRef.current) return;
    setError(outcome.error);
    if (outcome.error) return;
    applyToForm(outcome.chart);
  };

  const handleChangeSize = (nextSize: CustomChartSize) => applySize(nextSize);

  /** Начальное значение разреза: с него диаграмма показана до выбора пользователя на дашборде. */
  const handleChangeSliceValue = (value: ChartBindingValue) => setValue('sliceValue', value, { shouldDirty: true });

  /** Ведущая диаграмма: её выбор наполняет эту. Снятие связи возвращает диаграмму к подсказке. */
  const handleChangeSourceChart = (value: string | null) => setValue('sourceChartId', value, { shouldDirty: true });

  const handleChangeQuery = (query: string) => setRuleDraft((draft) => ({ ...draft, query }));

  const handleChangeTemplate = (template: string) => setRuleDraft((draft) => ({ ...draft, template }));

  /**
   * Ставит диаграмме оформление: и в форму, и на сетку — заданное видно сразу, как и размер.
   *
   * Оформление уходит в состав целиком, а не одним правленым полем: диаграмма состава принимает
   * его одной правкой, и порядок правок не разводит форму с сеткой.
   */
  const applyAppearance = (change: Partial<ChartAppearanceValues>) => {
    const next: ChartAppearanceValues = {
      barOrientation: change.barOrientation ?? barOrientation,
      categoryColors: change.categoryColors ?? categoryColors,
      valueColorRules: change.valueColorRules ?? valueColorRules,
    };
    setValue('barOrientation', next.barOrientation, { shouldDirty: true });
    setValue('categoryColors', next.categoryColors, { shouldDirty: true });
    setValue('valueColorRules', next.valueColorRules, { shouldDirty: true });
    onRestyle(next);
  };

  const handleChangeBarOrientation = (orientation: CustomChartBarOrientation) =>
    applyAppearance({ barOrientation: orientation });

  const handleChangeCategoryColors = (colors: ChartCategoryColors) => applyAppearance({ categoryColors: colors });

  const handleChangeValueColorRules = (rules: ChartValueColorRule[]) => applyAppearance({ valueColorRules: rules });

  const handleCancel = () => {
    onResize(initialSizeRef.current);
    // Оформление применялось к диаграмме сразу, поэтому отказ возвращает и его — наравне с
    // размером и прочими несохранёнными правками.
    onRestyle(initialAppearanceRef.current);
    onCancel();
  };

  // Правленый вручную запрос конкретнее описания, поэтому сохраняется он, а описание остаётся
  // подписью к диаграмме и заново не истолковывается.
  const editedQuery = ruleDraft.query.trim();
  const editedTemplate = ruleDraft.template.trim();
  const isQueryEdited = canEditQuery && editedQuery.length > 0 && editedQuery !== (chart.rule?.query ?? '').trim();

  /** Правило с правленым текстом результата: подписи он не меняет и заверения не требует. */
  const withEditedTemplate = (rule: ChartRule | null): ChartRule | null => {
    if (!rule || !isTemplateShown) return rule;
    return editedTemplate ? { ...rule, template: editedTemplate } : { query: rule.query };
  };

  const handleApply = handleSubmit(async (values) => {
    const edited: PresetChartFormValues = { ...values, rule: withEditedTemplate(values.rule) };
    // Описание к этому времени уже истолковано — сохранению свойств к бэкенду идти не за чем,
    // кроме заверения запроса, правленного вручную.
    if (!isQueryEdited) {
      onSave(edited);
      return;
    }
    const outcome = await signChart(edited, {
      query: editedQuery,
      ...(isTemplateShown && editedTemplate ? { template: editedTemplate } : {}),
    });
    if (!isBlockOpenRef.current) return;
    setError(outcome.error);
    if (outcome.error) return;
    onSave(outcome.chart);
  });

  return {
    control,
    chartType,
    size,
    description,
    summary,
    columns,
    ruleDraft,
    error,
    isResolving,
    isTemplateShown,
    isRowSectionShown,
    isQueryShown: canEditQuery,
    sliceBinding,
    sliceValue,
    isSourceChartShown,
    sourceChartId,
    barOrientation,
    categoryColors,
    valueColorRules,
    /** Категории последней полученной выборки — им задаются цвета */
    categories,
    /** Правило отвечает описанию и типу — свойства можно закреплять за дашбордом */
    isRuleActual,
    handleChangeDescription,
    handleResolveDescription,
    handleChangeType,
    handleChangeSize,
    handleChangeQuery,
    handleChangeTemplate,
    handleChangeSliceValue,
    handleChangeSourceChart,
    handleChangeBarOrientation,
    handleChangeCategoryColors,
    handleChangeValueColorRules,
    handleApply,
    handleCancel,
  };
};
